import React, { useEffect, useMemo, useState } from "react";
import MUITable from "Components/UI/Tables/MUITable";
import { Link } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import cutStrAtAndAddDots from "utils/cutStrAtAndAddDots";
import { price2StringPrice } from "utils/priceformatter";
import {
  getParseStatusLabelById,
  getSystemStatusLabelById,
} from "utils/labels";
import {
  getSystemStatusColorByLabel,
  getParseStatusColorByLabel,
} from "utils/colors";
import CommonChip from "Components/UI/Tables/CommonChip";
import { useClientPayments } from "Context/ClientPayments";
import { Checkbox, Collapse, Paper } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import { useClients } from "Context/Clients";
import useTableOptions from "Hooks/useTableOptions";

const createTableRows = (clientPayments, clients) => {
  return clientPayments?.map((cp) => {
    return [
      cp.Id,
      price2StringPrice(cp.Amount === null ? 0 : cp.Amount),
      {
        clientId: cp.ClientId,
        title: clients
          ? clients.find((c1) => c1.Id === cp.ClientId)?.Title
          : "",
      },
      cp.PaymentAccount,
      cp.PaymentType,
      cp.FileId,
      getParseStatusLabelById(cp.ParseStatus),
      cutStrAtAndAddDots(cp.ParseMessage, 45),
      getSystemStatusLabelById(cp.SystemStatus),
      cp.CreateTime,
    ];
  });
};

export default function ClientPaymentsDraftTable() {
  const {
    clientPayments,
    paymentNumber,
    getClientPaymentsDraft,
    confirmClientPayments,
    loading,
  } = useClientPayments();

  const { clients, fetchClients } = useClients();

  const [totalPaymentCount, setTotalPaymentCount] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [downloadFile, setDownloadFile] = useState(null);
  const { openError, openSuccess } = usePopupNotificationContext();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const { handleColumnShow, isColDisplayed } = useTableOptions(
    "ClientPaymentsTable"
  );

  useEffect(() => {
    getClientPaymentsDraft();
    fetchClients();
  }, []);

  useEffect(() => {
    const totPayment = clientPayments?.reduce(
      (partialSum, payment) => partialSum + payment.Amount,
      0
    );

    const rows = createTableRows(clientPayments, clients);
    setRows(rows);
    setTotalPayment(totPayment ? totPayment : 0);
    setTotalPaymentCount(clientPayments.length);
    forceCheckAll();
  }, [clientPayments, clients]);

  const onCheckAll = () => {
    if (selectedIds.length === clientPayments.length) {
      setSelectedIds([]);
    } else {
      const tmpSelectedIds = clientPayments?.map((payment) => payment.Id);
      setSelectedIds(tmpSelectedIds);
    }
  };

  const forceCheckAll = () => {
    const tmpSelectedIds = clientPayments?.map((payment) => payment.Id);
    setSelectedIds(tmpSelectedIds);
  };

  const onCheck = (id) => {
    if (selectedIds.findIndex((selectedId) => selectedId === id) > -1) {
      setSelectedIds([
        ...selectedIds.filter((selectedId) => selectedId !== id),
      ]);
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleOnBook = async () => {
    if (selectedIds.length > 0) {
      const res = await confirmClientPayments(selectedIds);
      if (res) {
        if (!res.data) {
          openError("Något gick fel. Ingen fil kunde skapas");
        } else {
          openSuccess("OK!");
          getClientPaymentsDraft();
          setDownloadFile(res.data);
        }
      } else {
        openError("Något gick fel");
      }
    }
  };

  const handleDownload = () => {
    if (downloadFile && downloadFile.Content) {
      setDownloadLoading(true);
      const binaryContent = Uint8Array.from(atob(downloadFile.Content), (c) =>
        c.charCodeAt(0)
      );

      const decoder = new TextDecoder("utf-8", { fatal: true });
      let decodedContent = decoder.decode(binaryContent);

      const blob = new Blob([decodedContent], {
        type: "application/xml;charset=utf-8",
      });

      const link = document.createElement("a");
      link.download = downloadFile.Name;
      link.href = URL.createObjectURL(blob);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadLoading(false);
    }
  };

  const formatPayment = (payment) => {
    const formattedNumber = new Intl.NumberFormat("se", {
      useGrouping: true,
      groupingSeparator: " ",
    }).format(payment);
    return formattedNumber;
  };

  const formattedPayment = useMemo(
    () => formatPayment(totalPayment),
    [totalPayment]
  );

  const columns = [
    {
      name: (
        <Checkbox
          onClick={onCheckAll}
          checked={selectedIds.length === clientPayments.length}
        />
      ),
      options: {
        filter: false,
        sort: false,
        viewColumns: false,
        customBodyRender: (value) => (
          <Checkbox
            checked={selectedIds.findIndex((id) => value === id) > -1}
            onClick={() => onCheck(value)}
          />
        ),
      },
    },
    {
      name: "Belopp",
      options: {
        filter: false,
        display: isColDisplayed("Belopp"),
      },
    },

    {
      name: "Klient",
      options: {
        empty: false,
        display: isColDisplayed("Klient"),
        customBodyRender: (value) => {
          return <Link to={`/clients/${value.clientId}`}>{value.title}</Link>;
        },
      },
    },
    {
      name: "Konto",
      options: {
        display: isColDisplayed("Konto"),
        filter: false,
      },
    },
    {
      name: "Kontotyp",
      options: {
        filter: false,
      },
    },
    {
      name: "Fil ID",
      options: {
        filter: true,
        empty: false,
        display: isColDisplayed("Fil ID"),
        customBodyRenderLite: (dataIndex) => {
          return (
            <Link to={`/files/${rows[dataIndex][5]}`}>
              {rows[dataIndex][5]}
            </Link>
          );
        },
      },
    },
    {
      name: "Kontrollstatus",
      options: {
        display: isColDisplayed("Kontrollstatus"),
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getParseStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Kontrollmeddelande",
      options: {
        display: isColDisplayed("Kontrollmeddelande"),
        filter: false,
      },
    },
    {
      name: "Status",
      options: {
        filter: true,
        sort: true,
        display: isColDisplayed("Status"),
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getSystemStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Skapad",
      options: {
        display: isColDisplayed("Skapad"),
        filter: false,
      },
    },
  ];

  const options = {
    rowsPerPage: 30,
    rowsPerPageOptions: [10, 30, 100],
    selectableRowsHeader: true,
    selectableRowsHideCheckboxes: true,
    viewColumns: false, // Disabled due to infinite re-renders
    onViewColumnsChange: (changedColumn, action) =>
      handleColumnShow(changedColumn, action),
    downloadOptions: {
      separator: ";",
      filename: "all_clients.csv",
    },

    onDownload: (buildHead, buildBody, columns, data) => {
      return "\uFEFF" + buildHead(columns) + buildBody(data);
    },
  };

  return (
    <>
      <Grid item container md={4} mb={2}>
        <Paper sx={{ padding: "10px", minWidth: "250px" }} elevation={4}>
          <Grid item container justifyContent={"space-between"}>
            <Grid item>
              <Typography
                fontWeight={"bold"}
              >{`Totalt (${totalPaymentCount}st):`}</Typography>
            </Grid>
            <Grid item>
              <Typography>{`${formattedPayment} kr`}</Typography>
            </Grid>
          </Grid>
          <Grid item container justifyContent={"space-between"}>
            <Grid item>
              <Typography fontWeight={"bold"}>
                {paymentNumber ? paymentNumber : "-"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item container justifyContent={"center"} my={2}>
        <Collapse in={!!downloadFile}>
          <Grid item>
            <LoadingButton
              variant="contained"
              loading={downloadLoading}
              onClick={handleDownload}
            >
              {downloadFile ? downloadFile?.Name : "Download file"}
            </LoadingButton>
          </Grid>
        </Collapse>
      </Grid>
      <MUITable options={options} title={""} data={rows} columns={columns} />
      <Grid item container justifyContent={"flex-end"} mt={2}>
        <Grid item>
          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={handleOnBook}
            disabled={selectedIds.length === 0}
          >
            Bokför
          </LoadingButton>
        </Grid>
      </Grid>
    </>
  );
}
